import { computed, Ref, ref, watch } from 'vue';
import { useAuraStore } from '@/stores/modules/aura';
import { AuraFilters } from '@/types';

/**
 * Composable for managing AURA filter state and logic
 * Encapsulates filter state, validation, and operations
 */
export function useAuraFilters() {
    const store = useAuraStore();
    const filterOpened = computed({
        get: () => store.isFiltersPanelOpen,
        set: (value: boolean) => {
            if (value) {
                store.openFiltersPanel();
            } else {
                store.closeFiltersPanel();
            }
        },
    });

    // Local filter state (ephemeral until applied)
    const draftFilters: Ref<AuraFilters> = ref({
        dateFrom: undefined,
        dateTo: undefined,
        operatorId: undefined,
        queueId: undefined,
        mediaId: undefined,
    });

    // Active filters from store (already applied)
    const activeFilters = computed(() => store.activeFilters);

    // Check if any filters are active
    const hasActiveFilters = computed(() => activeFilters.value !== null);

    // Count of active filters for badge display
    const activeFilterCount = computed(() => {
        const f = activeFilters.value;
        if (!f) return 0;

        let count = 0;
        if (f.dateFrom && f.dateTo) count++;
        if (f.operatorId) count++;
        if (f.queueId) count++;
        if (f.mediaId) count++;
        return count;
    });

    // Sync draft with store when panel opens
    watch(filterOpened, (isOpen) => {
        if (isOpen) {
            syncDraftWithStore();
        }
    });

    function syncDraftWithStore() {
        const storeFilters = store.activeFilters;
        if (storeFilters) {
            draftFilters.value = { ...storeFilters };
        } else {
            draftFilters.value = {
                dateFrom: undefined,
                dateTo: undefined,
                operatorId: undefined,
                queueId: undefined,
                mediaId: undefined,
            };
        }
    }

    function applyFilters() {
        store.setFilters(draftFilters.value);
        filterOpened.value = false;
    }

    function clearFilters() {
        draftFilters.value = {
            dateFrom: undefined,
            dateTo: undefined,
            operatorId: undefined,
            queueId: undefined,
            mediaId: undefined,
        };
        store.clearFilters();
    }

    function openFilterPanel() {
        store.openFiltersPanel();
    }

    function closeFilterPanel() {
        store.closeFiltersPanel();
    }

    return {
        // State
        filterOpened,
        draftFilters,
        activeFilters,
        hasActiveFilters,
        activeFilterCount,

        // Actions
        applyFilters,
        clearFilters,
        openFilterPanel,
        closeFilterPanel,
        syncDraftWithStore,
    };
}
